import { CanvasSource, Texture } from 'pixi.js';

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
};

/**
 * A texture that shows `element` by drawing each of its frames onto a 2D
 * canvas first. WebKit drops an HEVC-with-alpha video's alpha when WebGL
 * takes the frame from the {@code <video>} itself, which is how Pixi's
 * video textures upload, so the clip would show on a black box again; a
 * 2D canvas keeps the alpha. The canvas takes the video's size once it's
 * known, and is redrawn for every frame the video paints (while it plays)
 * and after a seek, so it costs nothing while the video is paused.
 */
export function canvasVideoTexture(element: HTMLVideoElement): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const source = new CanvasSource({ resource: canvas });
  const texture = new Texture({ source });
  const context = canvas.getContext('2d');

  const draw = (): void => {
    const width = element.videoWidth;
    const height = element.videoHeight;
    if (!context || !width || !height || element.readyState < 2) return;
    if (source.pixelWidth !== width || source.pixelHeight !== height) source.resize(width, height, 1);
    context.clearRect(0, 0, width, height);
    context.drawImage(element, 0, 0, width, height);
    source.update();
  };

  const video = element as FrameCallbackVideo;
  if (typeof video.requestVideoFrameCallback === 'function') {
    const onFrame = (): void => {
      draw();
      video.requestVideoFrameCallback!(onFrame);
    };
    video.requestVideoFrameCallback(onFrame);
  } else {
    // No frame callbacks: draw on every animation frame while it plays.
    let raf = 0;
    const loop = (): void => {
      draw();
      raf = element.paused || element.ended ? 0 : requestAnimationFrame(loop);
    };
    element.addEventListener('play', () => {
      if (!raf) raf = requestAnimationFrame(loop);
    });
  }
  element.addEventListener('loadeddata', draw);
  element.addEventListener('seeked', draw);
  return texture;
}
